/**
 * Reactions shown under every forum post.
 *
 * The buttons live inside a form which works without JavaScript, the module
 * only turns the round trip into an optimistic in place update.
 */

import { text } from "../core/i18n";
import { query, queryAll } from "../core/dom";
import { postJson } from "../core/http";
import { toast } from "../core/toast";
import type { ReactionKind, ReactionState } from "../core/types";
import { REACTION_KINDS } from "../core/types";

const ACTIVE_CLASS = "is-active";
const OPEN_CLASS = "is-open";
const EMPTY_CLASS = "is-empty";
const ERROR_FALLBACK = "Reakciu sa nepodarilo uložiť, skúste to prosím znova.";

/** Return whether `value` is one of the supported reaction kinds. */
export function isReactionKind(value: string): value is ReactionKind {
    return (REACTION_KINDS as readonly string[]).includes(value);
}

/** Return the reaction buttons of `bar` which are not part of the picker. */
function barButtons(bar: HTMLElement): HTMLElement[] {
    return queryAll<HTMLElement>("[data-reaction]", bar).filter(
        (button) => button.closest("[data-reaction-picker]") === null,
    );
}

/** Read the state currently rendered inside `bar`. */
export function readReactionState(bar: HTMLElement): ReactionState {
    const counts: Record<string, number> = {};
    const mine: string[] = [];
    let total = 0;

    for (const button of barButtons(bar)) {
        const kind = button.dataset["kind"] ?? "";
        if (!isReactionKind(kind)) {
            continue;
        }

        const label = query<HTMLElement>(".reaction__count", button);
        const parsed = Number.parseInt((label?.textContent ?? "0").trim(), 10);
        const count = Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;

        counts[kind] = count;
        total += count;

        if (button.classList.contains(ACTIVE_CLASS)) {
            mine.push(kind);
        }
    }

    return { total, counts, mine };
}

/** Return the state after the visitor toggled `kind` on `state`. */
export function toggleReaction(state: ReactionState, kind: ReactionKind): ReactionState {
    const counts: Record<string, number> = { ...state.counts };
    const active = state.mine.includes(kind);
    const current = counts[kind] ?? 0;

    counts[kind] = Math.max(active ? current - 1 : current + 1, 0);

    return {
        total: Math.max(active ? state.total - 1 : state.total + 1, 0),
        counts,
        mine: active ? state.mine.filter((entry) => entry !== kind) : [...state.mine, kind],
    };
}

/** Render `state` into the reaction bar `bar`. */
export function applyReactionState(bar: HTMLElement, state: ReactionState): void {
    for (const button of barButtons(bar)) {
        const kind = button.dataset["kind"] ?? "";
        if (!isReactionKind(kind)) {
            continue;
        }

        const count = state.counts[kind] ?? 0;
        const label = query<HTMLElement>(".reaction__count", button);

        if (label) {
            label.textContent = String(count);
        }

        const active = state.mine.includes(kind);
        button.classList.toggle(ACTIVE_CLASS, active);
        button.setAttribute("aria-pressed", active ? "true" : "false");

        // A reaction nobody left stays in the bar so that it can appear the
        // moment somebody clicks it, it is only faded out by the stylesheet.
        button.classList.toggle(EMPTY_CLASS, count === 0 && !active);
    }

    const total = query<HTMLElement>("[data-reaction-total]", bar);
    if (total) {
        total.textContent = String(state.total);
    }
}

/** Wire a single reaction bar. */
function initReactionBar(bar: HTMLElement): void {
    const postId = bar.dataset["postId"] ?? "";
    if (!postId) {
        return;
    }

    const endpoint = bar.dataset["reactionUrl"] ?? `/api/forum/posts/${postId}/reactions`;
    const picker = query<HTMLElement>("[data-reaction-picker]", bar);

    bar.addEventListener("click", (event: Event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const adder = target.closest<HTMLElement>("[data-reaction-add]");
        if (adder) {
            event.preventDefault();
            bar.classList.toggle(OPEN_CLASS);
            adder.setAttribute("aria-expanded", bar.classList.contains(OPEN_CLASS) ? "true" : "false");
            picker?.classList.toggle(OPEN_CLASS);

            return;
        }

        const button = target.closest<HTMLElement>("[data-reaction]");
        if (!button) {
            return;
        }

        const kind = button.dataset["kind"] ?? "";
        if (!isReactionKind(kind)) {
            return;
        }

        event.preventDefault();
        bar.classList.remove(OPEN_CLASS);
        picker?.classList.remove(OPEN_CLASS);

        const previous = readReactionState(bar);
        applyReactionState(bar, toggleReaction(previous, kind));

        void postJson<ReactionState>(endpoint, { kind })
            .then((payload) => applyReactionState(bar, payload))
            .catch(() => {
                applyReactionState(bar, previous);
                toast(text("js.reactions.error", ERROR_FALLBACK), "error");
            });
    });
}

/** Wire every reaction bar present on the page. */
export function initReactions(): void {
    for (const bar of queryAll<HTMLElement>("[data-reaction-bar]")) {
        initReactionBar(bar);
    }
}
